import React from 'react';
import clsx from 'clsx';
import { ERROR_TYPE, FEATURES, SEARCH_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { GridSkeleton } from '@components/basics/GridSkeleton';
import { useMediaQuery } from '@context/mediaQuery';
import { ParamsStore } from '@context/params';
import { gql, TopResultsDocument, useQuery } from '@graphql/reactQuery';
import { useGetTopSearchResults } from '@hooks/useGetTopSearchResults';
import { useLogError } from '@hooks/useLogErrorNew';
import { scrollToTop } from '@utils/helpers';
import { NoResultsExplorePage } from '../NoResultsExplorePage';
import {
  ArtistSearchCard,
  CollectorSearchCard,
  PlaylistSearchCard,
  ReleaseSearchCard,
} from '../SearchCards';
import { TopResultsCarousel } from '../TopResultsCarousel';
import { SEARCH_TAB_CHOICES } from '../schema';
import { setActiveTab } from '../useSearchPageStore';
import { AllResultsCarousel } from './AllResultsCarousel';
import { AllResultsMobileSkeleton } from './AllResultsMobileSkeleton';

export const ALL_RESULTS_LENGTH = 5;

/*#__PURE__*/ gql(/* GraphQL */ `
  query TopResults($input: SearchInput!, $pagination: SearchConnectionArgs) {
    search(input: $input) {
      id
      releasesPaginated(pagination: $pagination) {
        edges {
          node {
            __typename
            track {
              id
            }
            id
            type
            ...SearchResultRelease
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }

      artistsPaginated(pagination: $pagination) {
        edges {
          node {
            __typename
            id
            ...SearchResultArtist
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }

      collectors(pagination: $pagination) {
        edges {
          node {
            __typename
            id
            ...SearchResultCollector
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }

      shelves(pagination: $pagination) {
        edges {
          node {
            __typename
            id
            releaseCount
            ...PlaylistCardDetails
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`);

export const AllResults = ({
  searchInput,
  isAudioPlayerHidden,
}: {
  searchInput: string;
  isAudioPlayerHidden: boolean;
}) => {
  const logError = useLogError();
  const { tablet } = useMediaQuery();

  const { data: topResults, isLoading } = useQuery(TopResultsDocument, {
    variables: {
      input: {
        text: searchInput,
      },
      pagination: {
        first: ALL_RESULTS_LENGTH,
      },
    },
    enabled: !!searchInput,
    staleTime: 0,
    onError(error) {
      const errorMessage = 'Error while fetching top search results';

      logError({
        action: SEARCH_ERROR_ACTIONS.TOP_SEARCH_RESULTS_FETCH_ERROR,
        error,
        closeProcessingModal: true,
        message: errorMessage,
        errorType: ERROR_TYPE.FETCH_SEARCH_RESULTS_FAILED,
        feature: FEATURES.SEARCH,
        isArtistOnlyPage: false,
        level: 'fatal',
        toast: errorMessage,
      });
    },
  });

  const topSearchResults = topResults?.data?.search;

  const isEmptyInput = searchInput === '';

  const {
    hasReleases,
    hasArtists,
    hasPlaylists,
    hasCollectors,
    topRelease,
    topPlaylist,
    topArtist,
    topCollector,
  } = useGetTopSearchResults({ topSearchResults });

  const rowInMobile = !tablet;

  /*
   * If search input is empty, we want to show the top 5 artistResults,
   * releaseResults, playlistResults, and collectorResults. Otherwise
   * we should show the top 5 from each category from the search results
   */

  const soundsSlides =
    topSearchResults &&
    topSearchResults.releasesPaginated.edges.slice(0, ALL_RESULTS_LENGTH).map((release, index) => {
      return {
        id: index.toString(),
        slide: (
          <ReleaseSearchCard
            key={release.node.id}
            data={release.node}
            getNextTrackIds={undefined}
            rowInMobile={rowInMobile}
          />
        ),
      };
    });

  const playlistSlides =
    topSearchResults &&
    topSearchResults.shelves.edges.slice(0, ALL_RESULTS_LENGTH).map((playlist, index) => {
      return {
        id: index.toString(),
        slide: (
          <PlaylistSearchCard
            key={playlist.node.id}
            data={playlist.node}
            rowInMobile={rowInMobile}
          />
        ),
      };
    });

  const artistSlides =
    topSearchResults &&
    topSearchResults.artistsPaginated.edges.slice(0, ALL_RESULTS_LENGTH).map((artist, index) => {
      return {
        id: index.toString(),
        slide: (
          <ArtistSearchCard key={artist.node.id} artist={artist.node} rowInMobile={rowInMobile} />
        ),
      };
    });

  const collectorSlides =
    topSearchResults &&
    topSearchResults.collectors.edges.slice(0, ALL_RESULTS_LENGTH).map((collector, index) => {
      return {
        id: index.toString(),
        slide: (
          <CollectorSearchCard
            key={collector.node.id}
            collector={collector.node}
            rowInMobile={rowInMobile}
          />
        ),
      };
    });

  const topResultsSlides = [
    {
      id: '1',
      slide: !!topRelease ? (
        <ReleaseSearchCard
          data={topRelease}
          getNextTrackIds={undefined}
          rowInMobile={rowInMobile}
        />
      ) : (
        <></>
      ),
    },
    {
      id: '2',
      slide: !!topPlaylist ? (
        <PlaylistSearchCard data={topPlaylist} rowInMobile={rowInMobile} />
      ) : (
        <></>
      ),
    },
    {
      id: '3',
      slide: !!topArtist ? (
        <ArtistSearchCard artist={topArtist} rowInMobile={rowInMobile} />
      ) : (
        <></>
      ),
    },
    {
      id: '4',
      slide: !!topCollector ? (
        <CollectorSearchCard collector={topCollector} rowInMobile={rowInMobile} />
      ) : (
        <></>
      ),
    },
  ];

  const hasNoResults = !hasArtists && !hasReleases && !hasCollectors && !hasPlaylists;

  const showNoResultsPage = (!topSearchResults || hasNoResults) && !isEmptyInput;

  return (
    <div className="w-full px-4 pt-4 md:px-6">
      {isLoading ? (
        <>
          <div className="hidden md:block">
            <GridSkeleton />
          </div>
          <AllResultsMobileSkeleton />
        </>
      ) : showNoResultsPage ? (
        <NoResultsExplorePage searchInput={searchInput} />
      ) : (
        <div
          className={clsx(
            !isAudioPlayerHidden && 'pb-10 md:pb-0',
            'mb-3 flex flex-col gap-3 md:mb-0 md:gap-6',
          )}
        >
          <TopResultsCarousel header="Top Results" slides={topResultsSlides} />
          <div className="flex flex-col gap-6">
            {soundsSlides && soundsSlides?.length > 0 && (
              <AllResultsCarousel
                header="Sounds"
                slides={soundsSlides}
                onClick={() => {
                  scrollToTop();
                  setActiveTab({ activeTab: SEARCH_TAB_CHOICES.SOUNDS });
                  ParamsStore.tab = SEARCH_TAB_CHOICES.SOUNDS;
                }}
              />
            )}
            {playlistSlides && playlistSlides?.length > 0 && (
              <AllResultsCarousel
                header="Playlists"
                slides={playlistSlides}
                onClick={() => {
                  scrollToTop();
                  setActiveTab({ activeTab: SEARCH_TAB_CHOICES.PLAYLISTS });
                  ParamsStore.tab = SEARCH_TAB_CHOICES.PLAYLISTS;
                }}
              />
            )}
            {artistSlides && artistSlides?.length > 0 && (
              <AllResultsCarousel
                header="Artists"
                slides={artistSlides}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setActiveTab({ activeTab: SEARCH_TAB_CHOICES.ARTISTS });
                  ParamsStore.tab = SEARCH_TAB_CHOICES.ARTISTS;
                }}
              />
            )}
            {collectorSlides && collectorSlides?.length > 0 && (
              <AllResultsCarousel
                header="Collectors"
                slides={collectorSlides}
                onClick={() => {
                  scrollToTop();
                  setActiveTab({ activeTab: SEARCH_TAB_CHOICES.COLLECTORS });
                  ParamsStore.tab = SEARCH_TAB_CHOICES.COLLECTORS;
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
